/**
 * The two variables that decide whether a per-employee-priced programme pays,
 * and which the ROI model previously left out.
 *
 * Without them the return reduces to `enrolment x avoidance / price`, which is
 * independent of headcount and of the client's own medical spend, and which
 * assumes every member who signs up stays engaged for twelve months and is
 * drawn at random from the workforce. Neither holds.
 */
/**
 * Member-months of avoidance delivered in year one.
 *
 * A member only avoids spend while they are still engaged, so twelve sign-ups
 * are not twelve member-years. Retention is modelled as a constant monthly
 * hazard fitted to the twelve-month figure, which is the shape self-directed
 * health apps actually follow: steep early drop, long flat tail.
 *
 * `retentionAt12Months` of 1 returns 12 and reproduces the old arithmetic.
 */
export function effectiveMemberMonths(retentionAt12Months, months = 12) {
    const retained = clamp01(retentionAt12Months);
    if (retained >= 1)
        return months;
    if (retained <= 0)
        return 0;
    const monthly = retained ** (1 / months);
    // Sum of a geometric series: monthly + monthly^2 + ... + monthly^months.
    return (monthly * (1 - monthly ** months)) / (1 - monthly);
}
/**
 * Avoidance per enrolled member, given how well enrolment is targeted.
 *
 * At `targeting` 0 enrolment is a random draw from the workforce, so each
 * enrollee carries the population-weighted average, and members with no
 * addressable condition contribute nothing while still costing a fee. At 1
 * enrolment fills from the highest-avoidance group down. Real programmes sit
 * between: outreach can prioritise, but not everyone it reaches will enrol.
 *
 * This is the only lever that changes the answer without changing the price,
 * which is what Caloyeras found from the other direction when the combined arm
 * beat disease management alone and the authors put it down to targeting.
 */
export function avoidancePerEnrollee(cohorts, enrolmentShare, targeting) {
    const share = clamp01(enrolmentShare);
    if (share <= 0)
        return 0;
    const weighted = cohorts.reduce((sum, c) => sum + c.share * c.pmpmAvoided, 0);
    const focus = clamp01(targeting);
    if (focus <= 0)
        return weighted;
    // Perfect targeting: fill the enrolled seats from the richest group down.
    const ranked = [...cohorts].sort((a, b) => b.pmpmAvoided - a.pmpmAvoided);
    let remaining = share;
    let captured = 0;
    for (const cohort of ranked) {
        if (remaining <= 0)
            break;
        const taken = Math.min(cohort.share, remaining);
        captured += taken * cohort.pmpmAvoided;
        remaining -= taken;
    }
    // Seats past the last addressable member are filled by people with no
    // addressable condition. They avoid nothing, and they are already counted in
    // the denominator, so nothing is added for them here.
    const targeted = captured / share;
    return weighted + (targeted - weighted) * focus;
}
function clamp01(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.min(1, Math.max(0, value));
}
//# sourceMappingURL=roi-model.js.map